import { compressionCodecType, Dataset, LinkedService, Metamodel, Named, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { XmlSettings } from 'app/model/dataflow/dataset-settings/xml-settings';
import { XmlDataset } from 'app/model/dataset/xml.dataset';
import { ensureAllPathsAreUnique, getAdlsDatasetForPreview, initializeCommonConnectionProperties, processEachADLSEntity } from 'app/components/create-datamapper/utils/adls-component-utils';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IDataMapperFileOptions } from 'app/model/adfDataMapper/IDataMapperFileOptions';
import { delimitedTextEncodingNameOptionsForDataflow } from 'app/registries/consts/v2Dataset-const';
import { levelOptions } from 'app/shared/components/compression-setting/compression-options';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';

export class XmlDatasetTypeHandler extends DatasetTypeHandler {
    constructor(
        public isSource: Boolean
     ){
         super();
     }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('XmlSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as XmlSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as XmlSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.container.expression = !incomingSourceDatasetsSettings.container ? undefined : incomingSourceDatasetsSettings.container.expression;
        outputSourceDatasetsSettings.folderPath.expression = !incomingSourceDatasetsSettings.folderPath ? undefined : incomingSourceDatasetsSettings.folderPath.expression;
        outputSourceDatasetsSettings.compressionType = !incomingSourceDatasetsSettings.compressionType ? undefined : incomingSourceDatasetsSettings.compressionType;
        outputSourceDatasetsSettings.compressionLevel.expression = !incomingSourceDatasetsSettings.compressionLevel ? undefined : incomingSourceDatasetsSettings.compressionLevel.expression;
        outputSourceDatasetsSettings.encoding.expression = !incomingSourceDatasetsSettings.encoding ? undefined : incomingSourceDatasetsSettings.encoding.expression;
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        let disableButton = true;
        let disableButtonStatus = '';
        if (selectedRows.length) {
            disableButton = false;
            const rows = selectedRows as IMapperEntity[];
            rows.forEach((elem: IMapperEntity) => {
                const containerPath = (elem.dataset as XmlDataset).location.getLocation().container;
                if (containerPath === '') {
                    disableButton = true;
                }
            });
            if (!disableButton) {
                [disableButton, disableButtonStatus] = !ensureAllPathsAreUnique(rows) ? [true, this.isSource ? stringResources.DuplicateSourceEntitySelected : stringResources.DuplicateTargetEntitySelected] : [false, ''];
            }
            return [disableButton, disableButtonStatus];
        }
        return [disableButton, disableButtonStatus];
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            const filePath = (elem.dataset as XmlDataset).location.getLocation();
            processEachADLSEntity(filePath, selectedSourceLinkedService, elem, selectedDatasetFormat, tables, this.isSource);
        });

        return tables;
    }

    public getFileOptions(): IDataMapperFileOptions {
        return {
            compressionTypeOptions: [
                {
                    label: stringResources.None,
                    value: undefined
                },
                {
                    label: compressionCodecType.bzip2,
                    value: compressionCodecType.bzip2
                },
                {
                    label: compressionCodecType.gzip,
                    value: compressionCodecType.gzip
                },
                {
                    label: compressionCodecType.deflate,
                    value: compressionCodecType.deflate
                },
                {
                    label: compressionCodecType.snappy,
                    value: compressionCodecType.snappy
                },
                {
                    label: compressionCodecType.lz4,
                    value: compressionCodecType.lz4
                },
                {
                    label: compressionCodecType.TarGZip,
                    value: compressionCodecType.TarGZip
                },
                {
                    label: compressionCodecType.tar,
                    value: compressionCodecType.tar
                }
            ],
            compressionLevelOptions: levelOptions,
            encodingOptions: delimitedTextEncodingNameOptionsForDataflow
        };
    }

    public getDefaultFileSettings(): DataFlowDatasetSettings {
        const dataFlowSource = DataFlowSource.MODEL.create<Named>();
        const xmlSettings: XmlSettings = Owned.create<XmlSettings>(XmlSettings.MODEL, dataFlowSource);
        xmlSettings.initialize(undefined);
        return xmlSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        const compressionType = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionCodec);
        const compressionLevel = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionLevel);
        const encoding = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.encoding);

        // Create and populate XmlSettings
        const xmlDataset = Owned.create<XmlSettings>(XmlSettings.MODEL, table as DataFlowSource);
        xmlDataset.initialize(undefined);

        xmlDataset.container = createDataflowExpression(container) as DataFlowExpression;
        xmlDataset.folderPath = createDataflowExpression(folderPath) as DataFlowExpression;
        xmlDataset.compressionType = compressionType ?? undefined;
        if(!!compressionLevel) {
            xmlDataset.compressionLevel = createDataflowExpression(compressionLevel) as DataFlowExpression;
        }
        if(!!encoding) {
            xmlDataset.encoding = createDataflowExpression(encoding) as DataFlowExpression;
        }

        return xmlDataset;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        return getAdlsDatasetForPreview(DatasetFormatType.Xml, container, folderPath, linkedService);
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType, selectedSourceFileOptions: DataFlowDatasetSettings): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);
        initializeCommonConnectionProperties(mapperConnectionInfo.connection, selectedDatasetFormat, this.isSource);
        // Add xml dataset settings
        const xmlSettings = selectedSourceFileOptions as XmlSettings;
        if (this.isSource && !!xmlSettings.compressionType) {
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.compressionCodec, xmlSettings.compressionType);
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.compressionLevel, xmlSettings.compressionLevel.expression);
        }
        if (this.isSource && !!xmlSettings.encoding.expression) {
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.encodingName, xmlSettings.encoding.expression);
        }
    }
}
